import {DefinitionDangerZone as SharedDangerZone} from '@github-ui/custom-properties-core/components/DefinitionDangerZone'
import {DefinitionUsagesBanner} from '@github-ui/custom-properties-core/components/DefinitionUsagesBanner'
import {DeleteDefinitionDialog as SharedDeleteDefinitionDialog} from '@github-ui/custom-properties-core/components/DeleteDefinitionDialog'
import {OrgConflictsDialog as SharedOrgConflictsDialog} from '@github-ui/custom-properties-core/components/OrgConflictsDialog'
import {PromoteDefinitionDialog as SharedPromoteDefinitionDialog} from '@github-ui/custom-properties-core/components/PromoteDefinitionDialog'
import {useBannerContext} from '@github-ui/custom-properties-core/context/BannerContext'
import type {PropertyDefinition, PropertyNameOrgConflicts, SourceInfo} from '@github-ui/custom-properties-types'
import {customPropertyDetailsPath, enterprisePath, promoteBusinessOrgCustomPropertyPath} from '@github-ui/paths'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useNavigate} from '@github-ui/use-navigate'
import {useRef, useState} from 'react'

import {RESOURCE_CONFIG} from '../constants'
import {useCheckUsagePath} from '../hooks/use-check-usage-path'
import {useDeletePropertyPath, useListPropertiesPath} from '../hooks/use-properties-paths'
import {usePropertySource} from '../hooks/use-property-source'

interface Props {
  definition: PropertyDefinition
  business?: SourceInfo
  orgConflicts?: PropertyNameOrgConflicts
  canDelete: boolean
  canPromote: boolean
}

export function DefinitionDangerZone({definition, business, canDelete, canPromote, orgConflicts}: Props) {
  const deleteButtonRef = useRef<HTMLButtonElement>(null)
  const promoteButtonRef = useRef<HTMLButtonElement>(null)

  const [deleteDialogIsOpen, setDeleteDialogIsOpen] = useState(false)
  const [promoteDialogIsOpen, setPromoteDialogIsOpen] = useState(false)
  const [conflictDialogIsOpen, setConflictDialogIsOpen] = useState(false)

  const checkUsagePath = useCheckUsagePath(definition.propertyName)
  const deletePath = useDeletePropertyPath(definition.propertyName)
  const listPropertiesPath = useListPropertiesPath()
  const {pathPrefix, sourceName} = usePropertySource()

  const {setBanner} = useBannerContext()
  const navigate = useNavigate()

  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags */
  const showDefinitionUsages = useFeatureFlag('actions_oidc_settings_ui')

  const openPromotionDialog = () => {
    if (orgConflicts?.usages.length) {
      setConflictDialogIsOpen(true)
    } else {
      setPromoteDialogIsOpen(true)
    }
  }

  const promoActionDescription =
    business &&
    definition.source &&
    `This property is managed by ${definition.source.name}. Promote this property to make ${business.name} the owner.`

  return (
    <>
      <SharedDangerZone>
        {canDelete && (
          <SharedDangerZone.ActionItem
            title="Delete property"
            subtitle="Permanently delete this property and its values from all repositories."
            onClick={() => setDeleteDialogIsOpen(true)}
            variant="danger"
            ref={deleteButtonRef}
          />
        )}
        {canPromote && (
          <SharedDangerZone.ActionItem
            title="Promote to enterprise"
            subtitle={promoActionDescription}
            onClick={openPromotionDialog}
            ref={promoteButtonRef}
          />
        )}
      </SharedDangerZone>

      {deleteDialogIsOpen && (
        <SharedDeleteDefinitionDialog
          definition={definition}
          usagePath={checkUsagePath}
          deletePath={deletePath}
          targetLiterals={{single: 'repository', multiple: 'repositories'}}
          returnFocusRef={deleteButtonRef}
          renderUsageBanner={
            showDefinitionUsages
              ? usages => (
                  <DefinitionUsagesBanner
                    propertyName={definition.propertyName}
                    resourceUsages={usages?.resourceUsages}
                    resourceConfig={RESOURCE_CONFIG}
                    urlPrefix={`${pathPrefix}/${sourceName}`}
                  />
                )
              : undefined
          }
          onCancel={() => setDeleteDialogIsOpen(false)}
          onSuccess={() => {
            setBanner('definition.deleted.success')
            navigate(listPropertiesPath)
          }}
        />
      )}

      {promoteDialogIsOpen && business && (
        <SharedPromoteDefinitionDialog
          business={business}
          businessPath={enterprisePath(business)}
          promotePath={promoteBusinessOrgCustomPropertyPath({
            org: definition.source.slug,
            business: business.slug,
            propertyName: definition.propertyName,
          })}
          returnFocusRef={promoteButtonRef}
          onCancel={() => setPromoteDialogIsOpen(false)}
          onSuccess={() => {
            setBanner('definition.promotion.success')
            navigate(
              customPropertyDetailsPath({
                pathPrefix: 'enterprises',
                sourceName: business.slug,
                propertyName: definition.propertyName,
              }),
            )
          }}
        />
      )}

      {conflictDialogIsOpen && business && orgConflicts && (
        <SharedOrgConflictsDialog
          returnFocusRef={promoteButtonRef}
          title="Cannot promote to enterprise"
          displayMessage={`This property cannot be promoted to ${business.name} because there are conflicting properties`}
          orgConflicts={orgConflicts}
          onClose={() => setConflictDialogIsOpen(false)}
        />
      )}
    </>
  )
}
